import React, { FC } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Icon,
  HStack,
} from "@chakra-ui/react";
import { CheckCircle, ExternalLink } from "lucide-react";
import { useHistory } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

export interface ApplicationSuccessProps {
  jobTitle?: string;
  companyName?: string;
  jobId?: string;
  message?: string;
  showDashboardLink?: boolean;
}

const ApplicationSuccess: FC<ApplicationSuccessProps> = ({
  jobTitle,
  companyName,
  jobId,
  message = "You've already applied for this job!",
  showDashboardLink = true,
}) => {
  const history = useHistory();

  const handleBackToJob = () => {
    if (jobId) {
      history.push(`/jobs/${jobId}`);
    } else {
      history.push("/jobs");
    }
  };

  const handleGoToDashboard = () => {
    window.open(
      `${process.env.REACT_APP_PRO_APPLICATION_URL}/my-job-applications`,
      "_blank"
    );
  };

  return (
    <VStack spacing={0} maxW={"99vw"} minH={"100vh"}>
      <section className="px-3 lg:px-20 py-8 lg:py-16 w-full max-w-4xl">
        {/* Back button */}
        <div className="mb-8">
          <Button
            variant="link"
            colorScheme="prussianBlue"
            fontSize={{
              base: "14px",
              lg: "16px",
            }}
            onClick={handleBackToJob}
            className="mb-4 px-0"
            leftIcon={<Icon as={FaArrowLeft} w={4} h={4} />}
          >
            {jobId ? "Back to job" : "Back to jobs"}
          </Button>
        </div>

        {/* Success Section */}
        <Box py={8} textAlign="center">
          {/* Success Icon */}
          <Box mb={6}>
            <Icon
              as={CheckCircle}
              w={16}
              h={16}
              color="flatteredFlamingo.500"
              mx="auto"
            />
          </Box>

          {/* Main Heading */}
          <Heading
            as="h1"
            fontSize={{ base: "2xl", lg: "3xl" }}
            fontWeight="bold"
            color="#0E212D"
            mb={4}
          >
            Application Submitted!
          </Heading>

          {/* Message */}
          <Text
            fontSize={{ base: "md", lg: "lg" }}
            color="#6E6E6E"
            mb={6}
            maxW="md"
            mx="auto"
          >
            {message}
          </Text>

          {/* Job Info Card */}
          {jobTitle && (
            <Box
              border="1px"
              borderColor="#D7D7D7"
              bg="white"
              p={6}
              mb={6}
              maxW="md"
              mx="auto"
            >
              <Text
                fontSize={{ base: "lg", lg: "xl" }}
                fontWeight="medium"
                color="#121212"
                mb={2}
              >
                {jobTitle}
              </Text>
              {companyName && (
                <Text fontSize={{ base: "md", lg: "lg" }} color="#6E6E6E">
                  {companyName}
                </Text>
              )}
            </Box>
          )}

          {/* Additional Info */}
          <Text fontSize="sm" color="#6E6E6E" mb={8} maxW="md" mx="auto">
            Check your application status on your dashboard or go back to view
            other opportunities.
          </Text>
        </Box>

        {/* Action Buttons Section */}
        <Box py={{ base: 4, lg: 6 }}>
          <VStack spacing={4} maxW="md" mx="auto">
            {showDashboardLink && (
              <Button
                colorScheme="flatteredFlamingo"
                size="lg"
                w="full"
                rightIcon={<ExternalLink size={16} />}
                onClick={handleGoToDashboard}
                rounded={0}
                px={8}
                py={6}
              >
                View Dashboard
              </Button>
            )}

            <Button
              variant="outline"
              size="lg"
              w="full"
              onClick={handleBackToJob}
              rounded={0}
              px={8}
              py={6}
            >
              {jobId ? "Back to Job" : "Browse Jobs"}
            </Button>
          </VStack>
        </Box>
      </section>
    </VStack>
  );
};

export default ApplicationSuccess;
